import React, { useEffect } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useSelector } from 'react-redux';

import { yupResolver } from '@hookform/resolvers/yup';
import CloseIcon from '@mui/icons-material/Close';
import { Box, Dialog, IconButton, TextField, Typography } from '@mui/material';

import { PrimaryButton } from 'src/components/common/buttons/PrimaryButton';
import {
	IResetUserPasswordModalProps,
	IResetUserPasswordFormData,
} from 'src/features/user/domain/types';
import { ResetUserPasswordValidationScheme } from 'src/features/user/domain/validation';
import { USER_LOADING_KEY } from 'src/features/user/store/userThunks';

export const ResetUserPasswordModal: React.FC<IResetUserPasswordModalProps> = ({
	open,
	user,
	onClose,
	onSubmit,
}) => {
	const isLoading = useSelector(
		(state: { loading: ILoadingState }) =>
			state.loading.loadingStates[USER_LOADING_KEY],
	);

	const {
		control,
		handleSubmit,
		reset,
		formState: { errors },
	} = useForm<IResetUserPasswordFormData>({
		resolver: yupResolver(ResetUserPasswordValidationScheme),
		defaultValues: { new_password: '' },
	});

	useEffect(() => {
		if (open) {
			reset();
		}
	}, [open, reset]);

	if (!open) return null;

	return (
		<Dialog
			open={open}
			onClose={onClose}
			PaperProps={{
				sx: {
					borderRadius: 1,
					width: '610px',
					padding: '24px',
				},
			}}
		>
			<form onSubmit={handleSubmit(onSubmit)}>
				<Box
					sx={{
						display: 'flex',
						flexDirection: 'column',
						gap: 3,
						padding: 1,
					}}
				>
					<Box
						sx={{
							display: 'flex',
							justifyContent: 'space-between',
							alignItems: 'center',
							marginTop: -1.5,
						}}
					>
						<Typography variant='h6' fontWeight={500}>
							Reset Password
						</Typography>
						<IconButton
							aria-label='close'
							onClick={onClose}
							sx={{
								color: 'black',
							}}
						>
							<CloseIcon />
						</IconButton>
					</Box>

					<Typography color='#4E5056' sx={{ marginTop: -3 }}>
						Reset password of user{' '}
						<strong>
							{`${user.first_name} ${user.last_name}`}.
						</strong>
					</Typography>

					<Controller
						name='new_password'
						control={control}
						render={({ field }) => (
							<TextField
								{...field}
								label='New Password'
								variant='outlined'
								fullWidth
								type='password'
								error={!!errors.new_password}
								helperText={errors.new_password?.message}
							/>
						)}
					/>

					<PrimaryButton type='submit' isLoading={isLoading}>
						SAVE CHANGES
					</PrimaryButton>
				</Box>
			</form>
		</Dialog>
	);
};
